import { cloneElement, FC, isValidElement, ReactElement, ReactNode } from "react";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import CustomDocumentPreviewModal from "@/src/components/CustomUtils/CustomDocumentPreviewModal/CustomDocumentPreviewModal";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { reloadPageAfterNewDocumentConfirm } from "@/src/utils/admissionOffice/confirmNewDocumentReload";
import { DOCUMENT_CHECK_PREVIEW_MODAL_UI } from "@/src/constants/documentPreviewModalDefaults";

export interface AOPGeneratedDocumentToolbarProps {
  isGenerated: boolean;
  generatedPdfUrl: string | null;
  taskAttachedPdfUrl?: string | null;
  previewPdfUrl?: string | null;
  canOpenPdfPreview?: boolean;
  canOpenTaskDocumentEye?: boolean;
  /** Task has attached file in Document Check — lock Preview/Generate, enable download + eye on attachment. */
  isTaskDocumentCheckReview?: boolean;
  isGeneratedPdfPreviewOpen: boolean;
  setIsGeneratedPdfPreviewOpen: (open: boolean) => void;
  generatedPreviewTitle: string;
  onDownload: () => void;
  onApprove: () => void;
  onReject: () => void;
  previewButton?: ReactNode;
  generateButton: ReactNode;
  refreshButton?: ReactNode;
  /** Extra controls after the eye button row (e.g. Request letter “With Print”). */
  trailingSlot?: ReactNode;
}

const lockToolbarButton = (node: ReactNode, locked: boolean): ReactNode => {
  if (!locked || !isValidElement(node)) return node;
  const element = node as ReactElement<{ disabled?: boolean }>;
  return cloneElement(element, {
    disabled: locked || Boolean(element.props.disabled),
  });
};

/**
 * Shared Admissions Office toolbar: Preview / Generate / Download + eye (PDF modal with Approve/Reject).
 */
const AOPGeneratedDocumentToolbar: FC<AOPGeneratedDocumentToolbarProps> = ({
  isGenerated,
  generatedPdfUrl,
  taskAttachedPdfUrl = null,
  previewPdfUrl,
  canOpenPdfPreview,
  canOpenTaskDocumentEye,
  isGeneratedPdfPreviewOpen,
  setIsGeneratedPdfPreviewOpen,
  generatedPreviewTitle,
  onDownload,
  onApprove,
  onReject,
  previewButton,
  generateButton,
  refreshButton,
  trailingSlot,
  isTaskDocumentCheckReview = false,
}) => {
  const resolvedPreviewPdfUrl = previewPdfUrl ?? generatedPdfUrl;
  /** Eye: only Document Check + attached file. No fallbacks (ignore generated PDF / preview URL). */
  const canOpenEye = isTaskDocumentCheckReview === true && Boolean(taskAttachedPdfUrl);
  const canDownloadPdf = isTaskDocumentCheckReview
    ? Boolean(taskAttachedPdfUrl)
    : Boolean(isGenerated && generatedPdfUrl);
  /** Document Check eye popup always shows actions; auto-open after Generate keeps them too. */
  const showPreviewActions = isTaskDocumentCheckReview || Boolean(generatedPdfUrl);
  const modalDocumentUrl =
    generatedPdfUrl ?? (isTaskDocumentCheckReview && taskAttachedPdfUrl ? taskAttachedPdfUrl : resolvedPreviewPdfUrl);

  return (
    <>
      <div className="form-group-buttons">
        <div className="form-group-buttons-pack">
          {lockToolbarButton(previewButton, isTaskDocumentCheckReview)}
          {lockToolbarButton(generateButton, isTaskDocumentCheckReview)}
          <BtnDefault onClick={onDownload} disabled={!canDownloadPdf}>
            <p>Download PDF</p>
            <AODSvgExport.DownloadSvg />
          </BtnDefault>
        </div>
        <div className="form-group-buttons-pack">
          <BtnDefault
            onlyIcon={true}
            reverse={true}
            disabled={!canOpenEye}
            onClick={() => setIsGeneratedPdfPreviewOpen(true)}
          >
            <AODSvgExport.PreviewSvg />
          </BtnDefault>
          {refreshButton ?? (
            <div className="form-group-buttons-refresh" onClick={reloadPageAfterNewDocumentConfirm}>
              <BtnDefault onlyIcon={true}>
                <AODSvgExport.RefreshSvg />
              </BtnDefault>
            </div>
          )}
        </div>
        {trailingSlot}
      </div>

      <CustomDocumentPreviewModal
        isOpen={isGeneratedPdfPreviewOpen}
        documentUrl={modalDocumentUrl}
        onApprove={showPreviewActions ? onApprove : undefined}
        onReject={showPreviewActions ? onReject : undefined}
        onClose={() => setIsGeneratedPdfPreviewOpen(false)}
        {...DOCUMENT_CHECK_PREVIEW_MODAL_UI}
        title={generatedPreviewTitle}
        fileExtension="pdf"
      />
    </>
  );
};

export default AOPGeneratedDocumentToolbar;
